import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  EMPTY,
  Observable,
  catchError,
  defer,
  filter,
  from,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { concatMap } from 'rxjs/operators';

import { loadApprovalRequests } from '@/app/dashboard/pages/laws-and-policies/approvals/store/approval.actions';
import { selectActiveMode } from '@/app/dashboard/pages/laws-and-policies/approvals/store/approval.selectors';
import { loadPolicies } from '@/app/dashboard/pages/laws-and-policies/policies/store/policy.actions';
import * as TransactionActions from '@/app/dashboard/pages/transactions/store/transaction.actions';
import * as TransactionSelectors from '@/app/dashboard/pages/transactions/store/transaction.selectors';
import {
  isNetworkCost,
  TransactionProjectionStatusEnum,
} from '@/app/dashboard/pages/transactions/models/transaction.model';
import { loadUser } from '@/app/dashboard/store/dashboard.actions';
import { loadUserList } from '@/app/dashboard/pages/users/store/users.actions';
import { AuthService } from '@app/auth/services/auth.service';
import { TokenStorageService } from '@app/auth/services/token-storage.service';
import { forceLogout, logout } from '@app/auth/store/auth.actions';
import {
  PermissionService,
  getNotificationPermissionEntry,
} from '@app/core/permissions';
import { detectDeviceType } from '@app/core/realtime/device-type.util';
import { RealtimeService } from '@app/core/realtime/realtime.service';
import {
  ApprovalDecisionWsPayload,
  ApprovalRequestExpiredWsPayload,
  ApprovalRequestedWsPayload,
  ApprovalStageCompletedWsPayload,
  BlockchainConfirmationsUpdatedWsPayload,
  ConnectedDevice,
  DeviceDisconnectedPayload,
  DisconnectReason,
  DeviceListPayload,
  IdentifiedPayload,
  IdentifyFailedPayload,
  IdentifyFailureReason,
  KICK_DISCONNECT_REASONS,
  SigningProgressUpdatedWsPayload,
  SupportedLanguage,
  TransactionBroadcastedWsPayload,
  TransactionFailedWsPayload,
  TransactionFinalizedWsPayload,
  UserRoleChangedWsPayload,
  WalletBalanceUpdatedWsPayload,
  WalletStatusChangedWsPayload,
  WalletUpdatedWsPayload,
  WithdrawalOperationAddedWsPayload,
  WithdrawalOperationUpdatedWsPayload,
  WsEnvelope,
  WsEventType,
} from '@app/core/realtime/realtime.types';
import { LanguageService } from '@core/i18n/language.service';
import { ToastService } from '@app/shared/components/toast/services/toast.service';
import { loadVaults } from '@app/shared/store/vault/vault.actions';
import { loadWalletDetails } from '@app/shared/store/wallet/wallet.actions';
import { selectWalletDetails } from '@app/shared/store/wallet/wallet.selectors';
import {
  buildNotification,
  isUserFacingEvent,
} from '@shared/utils/build-notification';
import { notificationReceived } from '../notifications/notifications.actions';

import * as RealtimeActions from './realtime.actions';
import {
  selectCurrentUserId,
  selectRealtimeSocketInfo,
} from './realtime.selectors';

/** Kick reasons that revoke every session of a user, not one socket. */
const USER_WIDE_KICK_REASONS: readonly DisconnectReason[] = [
  DisconnectReason.LOGGED_OUT_ALL,
  DisconnectReason.PASSWORD_CHANGED,
];

/**
 * Is this DEVICE_DISCONNECTED about *us*? notification-management sends it to
 * every *other* socket of the account (`broadcastDeviceDisconnected` skips the
 * kicked one), so it is normally about a peer — another operator logging out,
 * one device revoked — and must only update the roster. It is ours when it
 * names our socket, or, for a user-wide revocation, our user (every session
 * of that user is ending). Matching by reason alone signed out every operator
 * of the account when one of them logged out (F-085).
 */
export function isOwnKick(
  event: DeviceDisconnectedPayload,
  me: Pick<IdentifiedPayload, 'socketId' | 'userId'> | null
): boolean {
  if (!me || !KICK_DISCONNECT_REASONS.includes(event.reason)) {
    return false;
  }
  if (event.socketId === me.socketId) {
    return true;
  }
  return (
    USER_WIDE_KICK_REASONS.includes(event.reason) && event.userId === me.userId
  );
}

@Injectable()
export class RealtimeEffects {
  private readonly actions$ = inject(Actions);
  private readonly realtime = inject(RealtimeService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly languageService = inject(LanguageService);
  private readonly authService = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store);

  /**
   * Bridge: every envelope from the socket becomes a typed action.
   * Subscribes lazily on the first effect activation; the subject lives in
   * the singleton service so re-subscribing is safe.
   */
  events$ = createEffect(() =>
    defer(() => this.realtime.events$).pipe(
      withLatestFrom(this.store.select(selectRealtimeSocketInfo)),
      map(([envelope, me]) => this.envelopeToAction(envelope, me))
    )
  );

  /**
   * Bridge: connection state transitions become state-changed actions.
   * Also detects server-initiated kicks (the disconnect arrives before any
   * IDENTIFY_FAILED) and converts them to `realtimeForceDisconnected`.
   */
  connectionState$ = createEffect(() =>
    defer(() => this.realtime.state$).pipe(
      map(state => RealtimeActions.realtimeConnectionStateChanged({ state }))
    )
  );

  /**
   * Lifecycle: open the socket when `realtimeConnect` fires.
   */
  connect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RealtimeActions.realtimeConnect),
        tap(() => {
          const accessToken = this.tokenStorage.getAccessToken();
          if (!accessToken) {
            return;
          }
          const language = (this.languageService.current() ??
            'fa') as SupportedLanguage;
          this.realtime.connect({
            accessToken,
            language,
            deviceType: detectDeviceType(),
          });
        })
      ),
    { dispatch: false }
  );

  /**
   * Lifecycle: tear down the socket on explicit disconnect, logout, or
   * forceLogout. Everything routes through this so the socket is never
   * leaked between sessions.
   */
  disconnect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RealtimeActions.realtimeDisconnect, logout, forceLogout),
        tap(() => this.realtime.disconnect('user'))
      ),
    { dispatch: false }
  );

  /**
   * After IDENTIFIED, mark the service as connected (cancel its watchdog).
   */
  identified$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RealtimeActions.realtimeConnected),
        tap(() => this.realtime.markIdentified())
      ),
    { dispatch: false }
  );

  /**
   * On IDENTIFY_FAILED:
   *  - TOKEN_EXPIRED → refresh the access token via AuthService, then reconnect.
   *  - everything else → forceLogout (terminal).
   */
  identifyFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealtimeActions.realtimeIdentifyFailed),
      switchMap(({ reason }) => {
        this.realtime.markFailed();
        if (reason === IdentifyFailureReason.TOKEN_EXPIRED) {
          return this.refreshAndReconnect$();
        }
        return of(forceLogout({ reason }));
      })
    )
  );

  /**
   * Server-initiated kick (SESSION_REVOKED, USER_LOGGED_OUT, LOGGED_OUT_ALL,
   * PASSWORD_CHANGED). These arrive on the socket as `DEVICE_DISCONNECTED`
   * peer events for *other* sockets, but for *our* socket they manifest as a
   * plain Socket.IO disconnect with no peer broadcast. The matching reason is
   * inferred from the `DEVICE_DISCONNECTED` event we receive for ourselves
   * just before the disconnect (when applicable) — surfaced by
   * `realtimeForceDisconnected`.
   */
  forceDisconnected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealtimeActions.realtimeForceDisconnected),
      map(({ reason }) => forceLogout({ reason }))
    )
  );

  /**
   * When the user switches language, drop the socket and re-IDENTIFY so the
   * server stores the new language preference for translated `message`
   * fields.
   *
   * We want to react in every state that represents an in-flight or
   * established session (CONNECTING, IDENTIFYING, CONNECTED, RECONNECTING,
   * DISCONNECTED) and skip only the two states that mean "there is no
   * session to re-identify":
   *   - IDLE: covers both the boot-time `translate.use(...)` emission (no
   *     socket yet) and the post-logout case (socket torn down).
   *   - FAILED: terminal state set during `forceLogout` teardown — issuing
   *     another connect here would race the logout flow.
   */
  languageSwitchReconnect$ = createEffect(() =>
    defer(() => this.translate.onLangChange).pipe(
      filter(
        () => this.realtime.state !== 'IDLE' && this.realtime.state !== 'FAILED'
      ),
      switchMap(() => [
        RealtimeActions.realtimeDisconnect(),
        RealtimeActions.realtimeConnect(),
      ])
    )
  );

  private refreshAndReconnect$(): Observable<Action> {
    const refreshToken = this.tokenStorage.getRefreshToken();
    if (!refreshToken) {
      return of(forceLogout({ reason: IdentifyFailureReason.TOKEN_EXPIRED }));
    }
    return this.authService.refreshToken(refreshToken).pipe(
      map(res => {
        const data = res?.data;
        const accessToken = data?.accessToken ?? null;
        const newRefreshToken = data?.refreshToken ?? null;
        if (!accessToken) {
          return forceLogout({ reason: IdentifyFailureReason.TOKEN_EXPIRED });
        }
        this.tokenStorage.save(accessToken, newRefreshToken);
        return RealtimeActions.realtimeConnect();
      }),
      catchError(() =>
        of(forceLogout({ reason: IdentifyFailureReason.TOKEN_EXPIRED }))
      )
    );
  }

  private envelopeToAction(
    envelope: WsEnvelope,
    me: IdentifiedPayload | null
  ): Action {
    switch (envelope.type) {
      case WsEventType.IDENTIFIED: {
        const payload = envelope.payload as IdentifiedPayload;
        return RealtimeActions.realtimeConnected({
          socketInfo: payload,
          envelope: envelope as WsEnvelope<IdentifiedPayload>,
        });
      }

      case WsEventType.IDENTIFY_FAILED: {
        const payload = envelope.payload as IdentifyFailedPayload;
        return RealtimeActions.realtimeIdentifyFailed({
          reason: payload.reason,
          envelope: envelope as WsEnvelope<IdentifyFailedPayload>,
        });
      }

      case WsEventType.DEVICE_LIST: {
        const payload = envelope.payload as DeviceListPayload;
        return RealtimeActions.realtimeDeviceListReceived({
          devices: payload.devices ?? [],
        });
      }

      case WsEventType.DEVICE_CONNECTED: {
        const device = envelope.payload as ConnectedDevice;
        return RealtimeActions.realtimeDeviceConnected({ device });
      }

      case WsEventType.DEVICE_DISCONNECTED: {
        const event = envelope.payload as DeviceDisconnectedPayload;
        // If the disconnected device is *this* socket and the reason is a
        // server-initiated kick, surface forceDisconnect; otherwise it's a
        // peer event for the devices list.
        if (isOwnKick(event, me)) {
          return RealtimeActions.realtimeForceDisconnected({
            reason: event.reason,
          });
        }
        return RealtimeActions.realtimeDeviceDisconnected({ event });
      }

      default:
        return RealtimeActions.realtimeEventReceived({ envelope });
    }
  }
}

/**
 * Maps incoming WS envelopes to feature actions. The whole module is a
 * routing table — each case spells out which slices need to re-fetch and
 * which side-effects (toast, notification) should run.
 *
 * Two effects share the same `realtimeEventReceived` source:
 *   - `dispatchFeatureActions$` emits feature load/refresh actions.
 *   - `notify$` writes to the notifications inbox and shows toasts.
 *
 * Both are kept side-by-side here so a future event type only needs one
 * edit.
 */
@Injectable()
export class RealtimeDispatcherEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly toast = inject(ToastService);
  private readonly permissions = inject(PermissionService);

  /**
   * Fan-out: a single event can produce multiple feature actions. We use
   * `concatMap` (not `mergeMap`) to preserve dispatch order per event, which
   * keeps reducer transitions deterministic in tests.
   */
  dispatchFeatureActions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealtimeActions.realtimeEventReceived),
      concatMap(({ envelope }) => this.actionsFor(envelope))
    )
  );

  /**
   * Pushes each user-facing event onto the in-memory notifications slice and
   * raises a toast for the high-signal categories. Toasts for non-critical
   * events would be noisy; only security and balance updates surface here.
   *
   * Role-based gate (added with the permission feature): the notification
   * matrix in `permissions.config.json` declares which roles may receive
   * each event id. Events the current user's role is not authorised to
   * receive are dropped here before they reach the bell or the toast
   * layer. The server still owns delivery authority — this is a
   * client-side hedge against role-changes that have not yet rolled out
   * to the WS publisher.
   *
   * Events with no entry in the notification matrix (likely a new event
   * type not yet documented) fall through and are surfaced as before so a
   * missing matrix row doesn't silence the bell entirely.
   */
  notify$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RealtimeActions.realtimeEventReceived),
        tap(({ envelope }) => {
          if (envelope.silent || !isUserFacingEvent(envelope.type)) {
            return;
          }
          if (!this.isAllowedByRole(envelope.type)) {
            return;
          }
          const item = buildNotification(envelope);
          if (item) {
            this.store.dispatch(notificationReceived({ item }));
          }
          this.maybeToast(envelope);
        })
      ),
    { dispatch: false }
  );

  /**
   * Two-stage check:
   *   1. If the event type has no entry in the notification matrix at
   *      all, allow it through. This avoids silently swallowing newly
   *      added event types while the matrix catches up.
   *   2. If the event type IS in the matrix, only allow it through when
   *      the current user's role is in the entry's `allowedRoles`.
   *
   * Reads happen synchronously off the store — the cost is one Map
   * lookup plus one Set lookup per event.
   */
  private isAllowedByRole(eventType: string): boolean {
    const entry = getNotificationPermissionEntry(eventType);
    if (!entry) {
      return true;
    }
    return this.permissions.snapshotCanReceiveNotification(eventType);
  }

  private actionsFor(envelope: WsEnvelope): Observable<Action> {
    switch (envelope.type) {
      // ---------- Account / user lifecycle (refresh user profile) ----------
      case WsEventType.USER_ACCOUNT_FROZEN:
      case WsEventType.USER_ACCOUNT_UNFROZEN:
      case WsEventType.USER_ACCOUNT_BLOCKED:
      case WsEventType.USER_ACCOUNT_UNBLOCKED:
      case WsEventType.ACCOUNT_FROZEN:
      case WsEventType.ACCOUNT_UNFROZEN:
      // Account-level block/unblock has the same shape as freeze/unfreeze
      // per the spec (same `AccountStatusWsPayload`) and needs the same
      // `accountStatus` widget refresh.
      case WsEventType.ACCOUNT_BLOCKED:
      case WsEventType.ACCOUNT_UNBLOCKED:
      // Email change updates the user record (the email field is part of
      // `loadUser`'s response). Refreshing the profile keeps account-settings
      // and the header initials in sync.
      case WsEventType.EMAIL_CHANGED:
      case WsEventType.KILL_SWITCH_EXECUTED:
      // Inverse of KILL_SWITCH_EXECUTED — published by user-management
      // when panic mode is cleared on unfreeze. Same data refresh shape
      // (the user's accountStatus needs to flip back from FROZEN), so
      // we reuse the same case body.
      case WsEventType.KILL_SWITCH_RELEASED:
        return from([loadUser()]);

      case WsEventType.USER_ROLE_CHANGED: {
        const payload = envelope.payload as UserRoleChangedWsPayload;
        const actions: Action[] = [loadUserList({})];
        // If our own role changed, reload the profile.
        this.runWithCurrentUserId(currentUserId => {
          if (currentUserId && payload.userId === currentUserId) {
            this.store.dispatch(loadUser());
          }
        });
        return from(actions);
      }

      case WsEventType.USER_ONBOARDED:
      case WsEventType.USER_DELETED:
        return from([loadUserList({})]);

      // ---------- Vault / wallet / whitelist ----------
      case WsEventType.VAULT_ENABLED:
      case WsEventType.VAULT_DISABLED:
      case WsEventType.VAULT_TOTAL_BALANCE_UPDATED:
      // Account-aggregate balance event — the spec marks it as the
      // *preferred* source for the dashboard `totalBalance` widget, but
      // we don't have a dashboard-overview slice yet, so the safest
      // refresh is the vaults summary which already exposes account-wide
      // totals. Once a dashboard slice exists, route this directly to
      // its load action and drop the vaults round-trip.
      case WsEventType.ACCOUNT_TOTAL_BALANCE_UPDATED:
      case WsEventType.WALLET_CREATED:
        return from([loadVaults({})]);

      case WsEventType.WALLET_UPDATED:
      case WsEventType.WALLET_STATUS_CHANGED:
      case WsEventType.WALLET_BALANCE_UPDATED: {
        const payload = envelope.payload as
          | WalletBalanceUpdatedWsPayload
          | WalletStatusChangedWsPayload
          | WalletUpdatedWsPayload;
        const actions: Action[] = [loadVaults({})];
        this.refreshWalletDetailIfOpen(payload.walletId, actions);
        return from(actions);
      }

      case WsEventType.WHITELIST_ADDRESS_CREATED:
      case WsEventType.WHITELIST_ADDRESS_ENABLED:
      case WsEventType.WHITELIST_ADDRESS_DISABLED:
      case WsEventType.WHITELIST_ADDRESS_DELETED:
        // The whitelist list lives in a feature slice that's only registered
        // on its route. No global selector to gate against; we re-fetch the
        // user-facing surfaces that may show whitelist data.
        return from([loadVaults({})]);

      // ---------- Withdrawal / transaction lifecycle ----------
      // The transaction detail page is driven entirely off these payloads:
      // each event patches the open detail's slices directly (gated in the
      // reducer on the wallet id) instead of triggering an HTTP refetch.
      case WsEventType.WITHDRAWAL_OPERATION_ADDED:
      case WsEventType.WITHDRAWAL_OPERATION_UPDATED: {
        const payload = envelope.payload as
          | WithdrawalOperationAddedWsPayload
          | WithdrawalOperationUpdatedWsPayload;
        const actions: Action[] = [];
        this.refreshTransactionListIfActive(actions);
        // Only UPDATED carries a status transition for the open detail; ADDED
        // is a brand-new withdrawal (different transaction) and must not flip
        // the open detail's status. The execution phase (EXECUTION_IN_PROGRESS)
        // maps to `null` here — its precise sub-status (SIGNING → BROADCASTING →
        // CONFIRMING) arrives via the dedicated signing/broadcast/confirmation
        // events below, which is the authoritative source.
        if (envelope.type === WsEventType.WITHDRAWAL_OPERATION_UPDATED) {
          const projectionState =
            RealtimeDispatcherEffects.mapWithdrawalStatusToProjectionState(
              payload.status
            );
          if (projectionState) {
            actions.push(
              TransactionActions.applyDetailStatusFromEvent({
                walletId: payload.walletId,
                projectionState,
              })
            );
          }
        }
        return from(actions);
      }

      case WsEventType.TRANSACTION_FINALIZED: {
        const payload = envelope.payload as TransactionFinalizedWsPayload;
        const actions: Action[] = [];
        this.refreshTransactionListIfActive(actions);
        this.refreshWalletDetailIfOpen(payload.walletId, actions);
        // Balance moves: also re-fetch the vault overview.
        actions.push(loadVaults({}));
        // Step 5 — flip the open detail to FINALIZED from the payload. Not for
        // a network cost: it is matched by wallet alone, and would flip the
        // wallet's open withdrawal (the nonce advance that cancels a refused
        // withdrawal finalizes while that withdrawal is on screen).
        if (!isNetworkCost(payload.kind)) {
          actions.push(
            TransactionActions.applyDetailStatusFromEvent({
              walletId: payload.walletId,
              projectionState: TransactionProjectionStatusEnum.FINALIZED,
            })
          );
        }
        return from(actions);
      }

      case WsEventType.TRANSACTION_FAILED: {
        const payload = envelope.payload as TransactionFailedWsPayload;
        const actions: Action[] = [];
        this.refreshTransactionListIfActive(actions);
        this.refreshWalletDetailIfOpen(payload.walletId, actions);
        actions.push(loadVaults({}));
        // Flip the open detail to FAILED, mapping the failure phase to the
        // code the stepper uses to infer which step failed.
        actions.push(
          TransactionActions.applyDetailStatusFromEvent({
            walletId: payload.walletId,
            projectionState: TransactionProjectionStatusEnum.FAILED,
            failure: {
              code: RealtimeDispatcherEffects.mapFailurePhaseToCode(
                payload.failurePhase
              ),
              reason: payload.failureReason ?? '',
            },
          })
        );
        return from(actions);
      }

      // ---------- Broadcast succeeded / TX hash available (step 4) ----------
      case WsEventType.TRANSACTION_BROADCASTED: {
        const payload = envelope.payload as TransactionBroadcastedWsPayload;
        const projectionState =
          (payload.status as TransactionProjectionStatusEnum) ??
          TransactionProjectionStatusEnum.CONFIRMING;
        return from([
          TransactionActions.applyNetworkDetailsFromEvent({
            walletId: payload.walletId,
            transactionHash: payload.txid,
            explorerUrl: payload.explorerUrl ?? null,
            projectionState,
            updatedAt: payload.occurredAt,
          }),
          TransactionActions.applyDetailStatusFromEvent({
            walletId: payload.walletId,
            projectionState,
          }),
        ]);
      }

      // ---------- Signing-progress (step 3) ----------
      case WsEventType.SIGNING_PROGRESS_UPDATED: {
        const payload = envelope.payload as SigningProgressUpdatedWsPayload;
        const isFailed = String(payload.milestone).toUpperCase() === 'FAILED';
        const projectionState = isFailed
          ? TransactionProjectionStatusEnum.FAILED
          : TransactionProjectionStatusEnum.SIGNING;
        const actions: Action[] = [
          TransactionActions.applySigningStatusFromEvent({
            walletId: payload.walletId,
            projectionState,
            updatedAt: payload.occurredAt,
          }),
        ];
        actions.push(
          TransactionActions.applyDetailStatusFromEvent({
            walletId: payload.walletId,
            projectionState,
            ...(isFailed
              ? {
                  failure: {
                    code: 'FAILED_SIGNING',
                    reason: payload.failureReason ?? '',
                  },
                }
              : {}),
          })
        );
        return from(actions);
      }

      // ---------- Blockchain confirmations (step 4) ----------
      case WsEventType.BLOCKCHAIN_CONFIRMATIONS_UPDATED: {
        const payload =
          envelope.payload as BlockchainConfirmationsUpdatedWsPayload;
        return from([
          TransactionActions.applyBlockchainDetailsFromEvent({
            walletId: payload.walletId,
            confirmCount: payload.confirmations,
            blockNo: payload.blockHeight ?? null,
            updatedAt: payload.occurredAt,
          }),
          // A confirmation increment implies the tx is being confirmed.
          TransactionActions.applyDetailStatusFromEvent({
            walletId: payload.walletId,
            projectionState: TransactionProjectionStatusEnum.CONFIRMING,
          }),
        ]);
      }

      // ---------- Policy & approvals ----------
      case WsEventType.POLICY_CREATED:
      case WsEventType.POLICY_UPDATED:
      case WsEventType.POLICY_DELETED:
      case WsEventType.POLICY_ACTIVATED:
      case WsEventType.POLICY_DEACTIVATED:
        return from([loadPolicies({})]);

      case WsEventType.POLICY_CREATE_REQUESTED:
      case WsEventType.POLICY_UPDATE_REQUESTED:
      case WsEventType.POLICY_ACTIVATE_REQUESTED:
      case WsEventType.POLICY_DEACTIVATE_REQUESTED:
      case WsEventType.POLICY_DELETE_REQUESTED:
        return this.approvalRefreshActions();

      case WsEventType.APPROVAL_REQUESTED:
      case WsEventType.APPROVAL_APPROVED:
      case WsEventType.APPROVAL_REJECTED:
      case WsEventType.APPROVAL_CANCELED:
      case WsEventType.APPROVAL_STAGE_COMPLETED:
      case WsEventType.APPROVAL_REQUEST_EXPIRED: {
        const payload = envelope.payload as
          | ApprovalRequestedWsPayload
          | ApprovalDecisionWsPayload
          | ApprovalStageCompletedWsPayload
          | ApprovalRequestExpiredWsPayload;
        // Refresh the approvals list, plus — when a transaction detail is
        // open whose `approvalRequestId` matches `payload.requestId` — the
        // per-tx approval-progress slice. The latter replaces the old
        // step-2 polling effect.
        const actions: Action[] = [];
        this.approvalRefreshActions()
          .subscribe(a => actions.push(a))
          .unsubscribe();
        this.refreshApprovalProgressIfMatching(payload.requestId, actions);
        return from(actions);
      }

      // ---------- Self-auth signals (toast handled in notify$) ----------
      case WsEventType.TOKEN_REUSE_DETECTED:
      case WsEventType.GOOGLE_AUTH_SETUP_COMPLETED:
      // Pair of `GOOGLE_AUTH_SETUP_COMPLETED` — same notify-only treatment.
      case WsEventType.GOOGLE_AUTH_DISABLED:
      case WsEventType.USER_PASSWORD_RESET:
      case WsEventType.PASSWORD_FORGET_TRIGGERED:
      case WsEventType.AUTH_LOGIN_SUCCESS:
      case WsEventType.SESSION_CREATED:
        return EMPTY;

      // Silent, owners only: the user-activity page's reducer counts these
      // straight off realtimeEventReceived to offer a reload.
      case WsEventType.LOGIN_RECORDED:
        return EMPTY;

      // ---------- Account lifecycle (informational) ----------
      case WsEventType.ACCOUNT_CREATED:
        return EMPTY;

      // Session/password/logout events for *this* user are also delivered
      // here — but the matching DEVICE_DISCONNECTED on our own socket has
      // already triggered forceLogout. For peers, no UI refresh is needed.
      case WsEventType.SESSION_REVOKED:
      case WsEventType.USER_LOGGED_OUT:
      case WsEventType.USER_LOGGED_OUT_ALL:
      case WsEventType.USER_PASSWORD_CHANGED:
        return EMPTY;

      default:
        return EMPTY;
    }
  }

  private approvalRefreshActions(): Observable<Action> {
    const actions: Action[] = [];
    this.store
      .select(selectActiveMode)
      .subscribe(mode => {
        actions.push(loadApprovalRequests(mode ? { mode } : {}));
      })
      .unsubscribe();
    return from(actions);
  }

  private refreshTransactionListIfActive(actions: Action[]): void {
    // The transactions slice is registered globally; firing `loadTransactions`
    // is safe — its effect reads filters from current state and is throttled
    // by the route already being subscribed to it. We still gate on whether
    // a list is populated to avoid kicking off a fetch on irrelevant pages.
    let hasListPopulated = false;
    this.store
      .select(TransactionSelectors.selectTransactions)
      .subscribe(list => {
        hasListPopulated = (list?.length ?? 0) > 0;
      })
      .unsubscribe();
    if (hasListPopulated) {
      actions.push(TransactionActions.loadTransactions({}));
    }
  }

  /**
   * Map a withdrawal-operation status (from `WITHDRAWAL_OPERATION_UPDATED`) to
   * the user-facing projection status.
   *
   * `EXECUTION_IN_PROGRESS` is emitted by vault-management exactly once, the
   * moment approval completes — at which point no on-chain transaction exists
   * yet, so the projection is `AWAITING_EXECUTION` (step 3, "choose fee &
   * execute"). The later, finer execution sub-states (SIGNING → BROADCASTING →
   * CONFIRMING) are owned by the dedicated signing/broadcast/confirmation
   * events and advance the step forward from there. The reducer's forward-only
   * guard protects against a duplicate of this event regressing a later step.
   *
   * Returns `null` for any unknown status so we never guess.
   */
  private static mapWithdrawalStatusToProjectionState(
    status: string | undefined
  ): TransactionProjectionStatusEnum | null {
    switch (
      String(status ?? '')
        .trim()
        .toUpperCase()
    ) {
      case 'WAITING_APPROVAL':
        return TransactionProjectionStatusEnum.PENDING;
      case 'EXECUTION_IN_PROGRESS':
        return TransactionProjectionStatusEnum.AWAITING_EXECUTION;
      case 'APPROVAL_REJECTED':
      case 'FAILED':
        return TransactionProjectionStatusEnum.FAILED;
      case 'APPROVAL_CANCELED':
        return TransactionProjectionStatusEnum.CANCELLED;
      case 'FINALIZED':
        return TransactionProjectionStatusEnum.FINALIZED;
      default:
        return null;
    }
  }

  /**
   * Map a `TRANSACTION_FAILED` failure phase to the failure `code` the stepper
   * uses (`mapFailureCodeToExecutionStep`) to infer which step failed.
   */
  private static mapFailurePhaseToCode(phase: string | undefined): string {
    switch (
      String(phase ?? '')
        .trim()
        .toUpperCase()
    ) {
      case 'EXECUTION_SIGNING':
        return 'FAILED_SIGNING';
      case 'EXECUTION_BROADCAST':
        return 'FAILED_BROADCASTING';
      default:
        return 'WITHDRAWAL_FAILED';
    }
  }

  /**
   * If a transaction detail is open whose `approvalRequestId` matches the
   * approval envelope's `requestId`, queue a silent re-fetch of the
   * approval-progress slice. Replaces the old step-2 polling effect; the
   * envelope tells us *exactly when* to refresh, so we only hit
   * policy-management on actual decisions instead of every 2s.
   */
  private refreshApprovalProgressIfMatching(
    requestId: string | undefined,
    actions: Action[]
  ): void {
    const externalRequestId = String(requestId ?? '').trim();
    if (!externalRequestId) {
      return;
    }
    let openApprovalRequestId: string | null = null;
    this.store
      .select(TransactionSelectors.selectDetailApprovalRequestId)
      .subscribe(id => {
        openApprovalRequestId = id;
      })
      .unsubscribe();
    if (openApprovalRequestId === externalRequestId) {
      actions.push(
        TransactionActions.refreshApprovalProgressByExternalId({
          externalRequestId,
        })
      );
    }
  }

  private refreshWalletDetailIfOpen(
    walletId: string | undefined,
    actions: Action[]
  ): void {
    if (!walletId) {
      return;
    }
    let openWalletId: string | null = null;
    this.store
      .select(selectWalletDetails)
      .subscribe(detail => {
        openWalletId = detail?.id ?? null;
      })
      .unsubscribe();
    if (openWalletId && openWalletId === walletId) {
      actions.push(
        loadWalletDetails({
          walletId,
          addresses: true,
          settings: true,
          assetNetwork: true,
          vault: true,
        })
      );
    }
  }

  private runWithCurrentUserId(fn: (userId: string | null) => void): void {
    this.store
      .select(selectCurrentUserId)
      .subscribe(id => fn(id))
      .unsubscribe();
  }

  /**
   * Raise a toast for events that warrant immediate user attention.
   *
   * Per the WEBSOCKET-EVENTS.md envelope contract, `message` is
   * server-pre-localized — no client-side translation needed. When it's
   * missing (legacy publisher that hasn't been updated) we suppress the
   * toast rather than surface a blank popup.
   */
  private maybeToast(envelope: WsEnvelope): void {
    const text = this.envelopeText(envelope);
    if (!text) {
      return;
    }
    switch (envelope.type) {
      case WsEventType.KILL_SWITCH_EXECUTED:
      case WsEventType.USER_ACCOUNT_FROZEN:
      case WsEventType.USER_ACCOUNT_BLOCKED:
      case WsEventType.ACCOUNT_FROZEN:
      // Account-level block is at least as severe as freeze — same
      // error treatment so it can't be missed.
      case WsEventType.ACCOUNT_BLOCKED:
      case WsEventType.TOKEN_REUSE_DETECTED:
      case WsEventType.TRANSACTION_FAILED:
      case WsEventType.APPROVAL_REQUEST_EXPIRED:
        this.toast.error(text);
        break;

      case WsEventType.USER_ACCOUNT_UNFROZEN:
      case WsEventType.USER_ACCOUNT_UNBLOCKED:
      case WsEventType.ACCOUNT_UNFROZEN:
      case WsEventType.ACCOUNT_UNBLOCKED:
      case WsEventType.TRANSACTION_FINALIZED:
      case WsEventType.APPROVAL_APPROVED:
      case WsEventType.GOOGLE_AUTH_SETUP_COMPLETED:
      case WsEventType.KILL_SWITCH_RELEASED:
        this.toast.success(text);
        break;

      case WsEventType.APPROVAL_REQUESTED:
      case WsEventType.APPROVAL_STAGE_COMPLETED:
      case WsEventType.WITHDRAWAL_OPERATION_ADDED:
      case WsEventType.WALLET_BALANCE_UPDATED:
      // Profile changes worth surfacing but not severe enough for a
      // success/error variant — informational fits best.
      case WsEventType.EMAIL_CHANGED:
        this.toast.info(text);
        break;

      case WsEventType.APPROVAL_REJECTED:
      case WsEventType.APPROVAL_CANCELED:
      // Disabling 2FA is a security-relevant downgrade; warn the user
      // (matching what the security tab itself warns about on opt-out).
      case WsEventType.GOOGLE_AUTH_DISABLED:
        this.toast.warning(text);
        break;

      default:
        // No toast — notification still appended to the bell inbox.
        break;
    }
  }

  /**
   * Resolve the displayable string for an envelope. The spec guarantees
   * `message` is set on every Kafka-sourced event; an empty string here
   * means a legacy publisher that hasn't been updated, in which case the
   * caller should suppress the surface entirely.
   */
  private envelopeText(envelope: WsEnvelope): string {
    return envelope.message ?? '';
  }
}
